/**
 * Tiny dependency-free pub/sub between the API layer (lib/api.ts) and AuthContext.
 *
 * WHY a separate module: AuthContext must react to things the axios interceptor observes (a
 * role-denied 403 means the UI may be showing a stale role; ACCOUNT_DEACTIVATED means the session
 * must end), but lib/api.ts already imports lib/auth.ts — importing AuthContext or the server-role
 * fetcher back into api.ts would create an import cycle. Both sides import only this file.
 */

export type AuthEvent =
  /** The server denied a request by role — re-fetch the authoritative server role. */
  | "authz-denied"
  /** The server reports the caller's account is deactivated — force logout. */
  | "account-deactivated";

type Listener = (event: AuthEvent) => void;

const listeners = new Set<Listener>();

/** Subscribe to auth events. Returns an unsubscribe function. */
export function onAuthEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitAuthEvent(event: AuthEvent): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch {
      // A faulty listener must never break the request pipeline that emitted the event.
    }
  }
}
